import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toEmbedUrl } from "@/lib/video";

const Btn = ({ label, href }) => {
  if (!label) return null;
  const internal = href && href.startsWith("/");
  const cls = "eyw-btn inline-flex";
  return internal ? <Link to={href} className={cls}>{label}</Link>
    : <a href={href || "#"} className={cls} target={internal ? undefined : "_blank"} rel="noreferrer">{label}</a>;
};

const Icon = ({ name, size = 22 }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C size={size} />;
};

// Renders one page-builder section block by type.
export default function SectionRenderer({ section }) {
  const p = section.props || {};
  switch (section.type) {
    case "hero":
      return (
        <section className="relative overflow-hidden" data-testid="section-hero">
          {p.image && <img src={p.image} alt="" className="absolute inset-0 h-full w-full object-cover" />}
          <div className={`relative ${p.image ? "bg-[#0F2A3C]/70" : "bg-[#144F75]"} px-6 lg:px-10 py-24 lg:py-32`}>
            <div className="max-w-[1200px] mx-auto">
              {p.eyebrow && <p className="text-[#D98E73] uppercase tracking-[0.2em] text-sm mb-3">{p.eyebrow}</p>}
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-tight">{p.heading}</h1>
              {p.subheading && <p className="text-white/80 text-lg mt-5 max-w-2xl">{p.subheading}</p>}
              <div className="mt-8 flex flex-wrap gap-4"><Btn label={p.cta_label} href={p.cta_href} /></div>
            </div>
          </div>
        </section>
      );
    case "text":
      return (
        <section className="px-6 lg:px-10 py-16" data-testid="section-text">
          <div className="max-w-[820px] mx-auto">
            {p.heading && <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B] mb-5">{p.heading}</h2>}
            <div className="text-[#4A5D6B] text-lg leading-relaxed whitespace-pre-wrap">{p.body}</div>
          </div>
        </section>
      );
    case "image":
      return (
        <section className={`${p.full_width ? "" : "px-6 lg:px-10"} py-10`} data-testid="section-image">
          <figure className={p.full_width ? "" : "max-w-[1100px] mx-auto"}>
            {p.url && <img src={p.url} alt={p.caption || ""} className="w-full rounded-2xl object-cover" />}
            {p.caption && <figcaption className="text-center text-sm text-[#8FA89B] mt-3">{p.caption}</figcaption>}
          </figure>
        </section>
      );
    case "features":
      return (
        <section className="px-6 lg:px-10 py-16 bg-[#F4F1EB]" data-testid="section-features">
          <div className="max-w-[1200px] mx-auto">
            {p.heading && <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B] mb-10 text-center">{p.heading}</h2>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(p.items || []).map((it, i) => (
                <div key={i} className="eyw-card p-7">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-[#144F75]/10 text-[#144F75] mb-4"><Icon name={it.icon} /></span>
                  <h3 className="text-lg text-[#1A242B] mb-1">{it.title}</h3>
                  <p className="text-[#4A5D6B] text-sm leading-relaxed">{it.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    case "cta_banner":
      return (
        <section className="px-6 lg:px-10 py-16" data-testid="section-cta">
          <div className="max-w-[1100px] mx-auto rounded-3xl bg-[#144F75] p-10 lg:p-14 text-center">
            <h2 className="font-serif text-3xl lg:text-4xl text-white">{p.heading}</h2>
            {p.subheading && <p className="text-white/80 mt-3 max-w-2xl mx-auto">{p.subheading}</p>}
            <div className="mt-7 flex flex-wrap gap-4 justify-center">
              <Btn label={p.button_label} href={p.button_href} />
            </div>
          </div>
        </section>
      );
    case "gallery":
      return (
        <section className="px-6 lg:px-10 py-12" data-testid="section-gallery">
          <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
            {(p.images || []).map((url, i) => <img key={i} src={url} alt="" className="w-full h-56 object-cover rounded-xl" />)}
          </div>
        </section>
      );
    case "embed": {
      const src = p.url ? toEmbedUrl(p.url) : "";
      return (
        <section className="px-6 lg:px-10 py-12" data-testid="section-embed">
          <div className="max-w-[900px] mx-auto aspect-video bg-black rounded-2xl overflow-hidden">
            {src && <iframe src={src} title="embed" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />}
          </div>
        </section>
      );
    }
    case "quote":
      return (
        <section className="px-6 lg:px-10 py-16" data-testid="section-quote">
          <blockquote className="max-w-[820px] mx-auto text-center">
            <p className="font-serif text-2xl lg:text-3xl text-[#1A242B] italic leading-snug">“{p.quote}”</p>
            {p.author && <footer className="text-[#8FA89B] mt-4">— {p.author}</footer>}
          </blockquote>
        </section>
      );
    case "spacer":
      return <div style={{ height: Number(p.height) || 48 }} data-testid="section-spacer" />;
    default:
      return null;
  }
}
